'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { env } from '@/env/env';
import { useAuth } from '../providers/AuthProvider';
import { TelegramAuthData } from '../types';
import telegramLogo from '@Assets/icons/telegram-logo.svg';

interface TelegramSignInButtonProps {
    className?: string;
    disabled?: boolean;
}

const TELEGRAM_BOT_USERNAME = env('NEXT_PUBLIC_TELEGRAM_BOT_USERNAME') ?? '';
const TELEGRAM_WIDGET_URL = env('NEXT_PUBLIC_TELEGRAM_WIDGET_URL') ?? 'https://telegram.org/js/telegram-widget.js?22';

export const TelegramSignInButton: React.FC<TelegramSignInButtonProps> = ({
    className = '',
    disabled = false,
}) => {
    const { loginWithTelegram } = useAuth();
    const callbackRef = useRef(loginWithTelegram);
    // Keep the latest callback in the ref via an effect: refs must not be written during render
    useEffect(() => {
        callbackRef.current = loginWithTelegram;
    }, [loginWithTelegram]);

    const widgetRef = useRef<HTMLDivElement>(null);
    const [widgetFailed, setWidgetFailed] = useState(false);

    // Register global callback that widget's __parseFunction creates a wrapper for
    useEffect(() => {
        (window as any).onTelegramAuth = (user: TelegramAuthData) => {
            callbackRef.current(user);
        };

        return () => {
            delete (window as any).onTelegramAuth;
        };
    }, []);

    useEffect(() => {
        if (!widgetRef.current) return;

        widgetRef.current.innerHTML = '';

        const script = document.createElement('script');
        script.src = TELEGRAM_WIDGET_URL;
        script.async = true;
        script.setAttribute('data-telegram-login', TELEGRAM_BOT_USERNAME);
        script.setAttribute('data-size', 'large');
        // Required: widget creates onAuthUser via __parseFunction from this attribute.
        // Without it, postMessageHandler's auth_user event has no callback to invoke.
        script.setAttribute('data-onauth', 'onTelegramAuth(user)');
        script.setAttribute('data-request-access', 'write');
        widgetRef.current.appendChild(script);

        // Validate: working widget sends postMessage (resize), broken one doesn't
        let received = false;

        const handleMessage = (event: MessageEvent) => {
            try {
                if (new URL(event.origin).hostname.endsWith('telegram.org')) {
                    received = true;
                    clearTimeout(timeout);
                    window.removeEventListener('message', handleMessage);
                }
            } catch {
                // Invalid origin
            }
        };

        window.addEventListener('message', handleMessage);

        const timeout = setTimeout(() => {
            if (!received) setWidgetFailed(true);
            window.removeEventListener('message', handleMessage);
        }, 5000);

        return () => {
            window.removeEventListener('message', handleMessage);
            clearTimeout(timeout);
        };
    }, []);

    if (widgetFailed) return null;

    return (
        <div
            className={className}
            style={{ position: 'relative', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
        >
            <Image src={telegramLogo} width={24} height={24} alt="Telegram" style={{ pointerEvents: 'none' }} />
            {/* Widget overlay: opacity >= 0.1 for widget's isVisible(), filter hides visually */}
            <div
                ref={widgetRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    opacity: 1,
                    filter: 'opacity(0)',
                    pointerEvents: disabled ? 'none' : 'auto',
                }}
            />
        </div>
    );
};

export default TelegramSignInButton;
